import phonics from "../data/phonicsData";
import words from "../data/wordsData";
import { saveUserStats } from "../api/gameService";

import heart0 from "../assets/images/health-0.svg";
import heart25 from "../assets/images/health-25.svg";
import heart50 from "../assets/images/health-50.svg";
import heart75 from "../assets/images/health-75.svg";
import heart100 from "../assets/images/health-100.svg";

import battery0 from "../assets/images/Battery-0.svg";
import battery25 from "../assets/images/Battery-25.svg";
import battery50 from "../assets/images/Battery-50.svg";
import battery75 from "../assets/images/Battery-75.svg";
import battery100 from "../assets/images/Battery-100.svg";
import smiley from "../assets/images/Smiley.svg";

export const getLevelData = (level) => {
  if (!level) return null;

  return {
    1: "levelOne",
    2: "levelTwo",
    3: "levelThree",
    4: "levelFour",
    5: "levelFive",
  }[level];
};

/**
 * Get a list of phonics from the static array, based on the level.
 * @param {*} level
 * @returns
 */
const getStaticArray = (level, game) => {
  const levelData = getLevelData(level);
  if (game == "swing") {
    return Object.keys(phonics[levelData]);
  } else if (game == "blending") {
    return Object.keys(words[levelData]);
  }
};

/**
 * Shuffle that array. Nice and simple algorithm.
 * Code courtesy of Geeks for Geeks.
 * @param {*} array
 * @returns
 */
const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    // Generate random number
    let j = Math.floor(Math.random() * (i + 1));

    let temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }

  return array;
};

export const getPhonemeArray = (word, level) => {
  return words[level][word].phonics;
};

/**
 * Get the array of sounds for the named game.
 * Attempts to return a mix of incorrect, correct and new sounds for the given child, based on their
 * past results. Basic algo at the moment!
 * @param {*} id - user id
 * @param {*} level - the level of the game
 * @param {*} game - the name of the game you want an array for eg swing, blending
 * @param {*} maxPerGame - the maximum number of phonemes/words you want to play with
 * @returns
 */

export const getArrayForGame = async (
  level,
  game,
  incorrect = [],
  correct = [],
  maxPerGame
) => {
  const MAX_INCORRECT_PER_GAME = 8;
  const MAX_CORRECT_PER_GAME = 4;

  const staticArray = getStaticArray(level, game);

  // all results
  const result = new Set();

  // add in at most MAX_INCORRECT_PER_GAME of incorrect answers
  for (const answer of incorrect) {
    result.add(answer.phonic);
    if (result.length >= MAX_INCORRECT_PER_GAME) {
      console.log("Break!! Reach max incorrect");
      break;
    }
  }

  const maxCorrect = result.length + MAX_CORRECT_PER_GAME;
  // add in at most MAX_CORRECT_PER_GAME of correct answers
  for (const round of correct) {
    result.add(round.phonic);
    if (result.length >= maxCorrect) {
      break;
    }
  }

  // fill up remaining
  let shuffledArray = [...staticArray];
  if (game == "blending") {
    shuffledArray = shuffleArray(staticArray);
  }

  for (let i = 0; i < shuffledArray.length; i++) {
    result.add(shuffledArray[i]);
    if (result.size >= maxPerGame) {
      break;
    }
  }

  const shuffled = shuffleArray(Array.from(result));

  return shuffled;
};

export const getPhonicResults = (roundsPlayed) => {
  //Get object of all phonics & results
  const phonicsObject = {};

  //Dates to check when round played
  const todaysDate = new Date();
  const timeLimit = new Date(1000 * 3600 * 24 * 30); //milliseconds in 30 days (~1 month)
  const dateLimit = todaysDate - timeLimit;

  for (let i = 0; i < roundsPlayed?.length; i++) {
    //Check round played within last 30 days
    const roundTime = roundsPlayed[i].roundTime;
    const roundMilliseconds =
      roundTime.seconds * 1000 + roundTime.nanoseconds / 10 ** 6;
    const roundDate = new Date(roundMilliseconds);

    if (roundDate - dateLimit < 0) {
      break;
    }

    //If so, add phonic to existing or new phonic object
    const currentPhonic = roundsPlayed[i].phonic;
    if (currentPhonic in phonicsObject) {
      phonicsObject[currentPhonic].total += 1;
      phonicsObject[currentPhonic].correct += roundsPlayed[i].correct ? 1 : 0;
    } else {
      phonicsObject[currentPhonic] = {
        name: currentPhonic,
        correct: roundsPlayed[i].correct ? 1 : 0,
        total: 1,
      };
    }
  }

  //Do some maths :)
  let topPhonic;
  let topPercent = 0;
  let bottomPhonic;
  let bottomPercent = 1;
  let mostImproved;
  let mostImprovedRatio = 0;

  for (let phonicKey in phonicsObject) {
    let phonic = phonicsObject[phonicKey];

    if (phonic) {
      phonic.percentCorrect = phonic.correct / phonic.total;

      if (phonic.percentCorrect > topPercent) {
        topPhonic = phonic.name;
        topPercent = phonic.percentCorrect;
      }

      if (phonic.percentCorrect < bottomPercent) {
        bottomPhonic = phonic.name;
        bottomPercent = phonic.percentCorrect;
      }

      const incorrect = phonic.total - phonic.correct;
      // Guard to make sure not dividing by 0
      if (incorrect == 0) {
        phonic.improvementRatio = 0;
        continue;
      }

      phonic.improvementRatio = phonic.correct / incorrect;
      if (phonic.improvementRatio > mostImprovedRatio) {
        mostImproved = phonic.name;
        mostImprovedRatio = phonic.improvementRatio;
      }
    }
  }
  return { topPhonic, bottomPhonic, mostImproved };
};

export const updateUserStats = (statToChange, user, setUser) => {
  switch (statToChange) {
    case "energy":
      if (user.energyLevel > 0) {
        setUser((prev) => ({ ...prev, energyLevel: --user.energyLevel }));
      }
      break;

    case "fuel":
      if (user.fuelLevel < 100) {
        setUser((prev) => ({ ...prev, fuelLevel: ++user.fuelLevel }));
      }
      break;

    case "health":
      if (user.healthLevel < 100) {
        setUser((prev) => ({ ...prev, healthLevel: ++user.healthLevel }));
      }
      break;

    default:
      break;
  }

  try {
    saveUserStats(user.uid, user.energyLevel, user.fuelLevel, user.healthLevel);
  } catch (e) {
    console.log("Error saving user stats");
  }
};

export const getNeedsIcon = (stat, needLevel) => {
  switch (stat) {
    case "energy":
      return smiley;

    case "fuel":
      return needLevel < 25
        ? battery0
        : needLevel < 50
        ? battery25
        : needLevel < 75
        ? battery50
        : needLevel < 100
        ? battery75
        : battery100;

    case "health":
      return needLevel < 25
        ? heart0
        : needLevel < 50
        ? heart25
        : needLevel < 75
        ? heart50
        : needLevel < 100
        ? heart75
        : heart100;

    default:
      break;
  }
};
